import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import { ContactDialog } from '../components/ContactDialog';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [freeToday, setFreeToday] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawRedirect = searchParams.get('redirect') || '/';
  // Never bounce a freshly-authenticated user to the paywall or back to login.
  // Send them to the app root and let the route guard decide access
  // (admins/active subscribers -> app; expired -> /pricing with banner).
  const redirect = (rawRedirect === '/pricing' || rawRedirect.startsWith('/login')) ? '/' : rawRedirect;

  // Public: learn if today is a free-access day so we can highlight the perk.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/pricing`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setFreeToday(!!d?.free_access_day))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirect);
    }
  }, [isAuthenticated, navigate, redirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      // Check if user needs to reset password
      if (userData.force_password_reset) {
        navigate('/change-password');
        return;
      }
      // Respect the user's "Default App" preference only when no explicit redirect
      // was requested. BetMaker requires access (backend already guards this).
      if (redirect === '/') {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betmaker/preferences`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            if (data?.preferences?.defaultApp === 'betmaker' && data?.betmakerEnabled) {
              navigate('/betmaker');
              return;
            }
          }
        } catch { /* ignore — fall through to default */ }
      }
      navigate(redirect);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4 py-10 gap-10">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">
            IntelliTix<span className="text-emerald-400">®</span> &amp; IntelliTix<sup>Pro</sup>
          </h1>
          <p className="text-slate-400 mt-2">Sign in to your account</p>
        </div>

        {/* Free-access promo — every Monday & Tuesday for all members */}
        <div
          data-testid="login-free-access-promo"
          className={`mb-6 rounded-xl border px-4 py-3 text-center ${
            freeToday
              ? 'bg-emerald-500/15 border-emerald-500/50'
              : 'bg-slate-800/40 border-slate-700'
          }`}
        >
          <p className="text-sm font-semibold text-emerald-400">
            {freeToday ? '🎉 Free Access Today!' : 'Free Every Monday & Tuesday'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {freeToday
              ? 'IntelliTix & IntelliTix Pro are free today for all members — just sign in.'
              : 'All members get IntelliTix & IntelliTix Pro free every Mon & Tue (ET).'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                placeholder="you@example.com"
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-300">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-emerald-400 hover:text-emerald-300 transition"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-12 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center"
              data-testid="login-submit"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-400">
            Don't have an account?{' '}
            <Link to="/signup" className="text-emerald-400 hover:text-emerald-300 font-medium transition">
              Sign up
            </Link>
            <p className="mt-1 text-emerald-400 font-medium" data-testid="free-trial-note">
              New Accounts Enjoy 7 Day Free Trial
            </p>
          </div>

          <div className="mt-4 text-center">
            <ContactDialog label="Contact Us" />
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-8">
          © 2026 IntelliTix. All rights reserved.
        </p>
      </div>

      {/* Public instructional videos — visible to everyone (logged-out home) */}
      <div className="w-full max-w-4xl" data-testid="intro-videos">
        <div className="text-center mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-white">See it in action</h2>
          <p className="text-slate-400 text-sm mt-1">
            Quick overviews of IntelliTix and BetMaker.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { id: '5-zDUXpb2FU', title: 'IntelliTix Overview' },
            { id: '7QMp4FB6ru0', title: 'BetMaker Overview' },
          ].map((v) => (
            <div key={v.id} data-testid={`video-${v.id}`}>
              <div className="aspect-video w-full overflow-hidden rounded-xl border border-slate-700 bg-black shadow-lg">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${v.id}`}
                  title={v.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="text-slate-300 text-sm font-medium mt-2 text-center">{v.title}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
